import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import TicketListModal from '../components/TicketListModal';
import TicketPeekDetail from '../components/TicketPeekDetail';
import './SLATrackingDashboard.css';
import { fmtDate } from '../lib/when';

/** The four SLA states, in the order they appear as tiles and as filters. */
const STATES = [
  { key: 'all', label: 'All', tile: 'Total Tickets', className: '' },
  { key: 'on_track', label: 'On Track', tile: 'On Track', className: 'on-track' },
  { key: 'at_risk', label: 'At Risk', tile: 'At Risk', className: 'at-risk' },
  { key: 'breached', label: 'Breached', tile: 'Breached', className: 'breached' }
];

export default function SLATrackingDashboard() {
  const [slaData, setSlaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [drilldown, setDrilldown] = useState(null);
  // A failed read used to leave this board showing zero of everything, which
  // reads as "nothing is breaching" rather than "this did not load".
  const [error, setError] = useState(null);
  // The one card whose ticket is open. A card here is a summary of a clock;
  // when a clock is running out the next question is always what the ticket
  // actually says, and until now that meant leaving the page to find out.
  const [peek, setPeek] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const fetchSLAData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // No Authorization header is built here. lib/apiClient patches axios and
      // fetch and attaches the real staff token to every call to this API, and
      // it treats a hand-built `Bearer null` as not-a-credential precisely
      // because several pages used to send one. The token this page read has
      // not been written by anything since staff sign-in moved to Entra.
      const response = await axios.get(`${API_BASE}/sla-tracking`);
      setSlaData(response.data || []);
    } catch (error) {
      console.error('Error fetching SLA data:', error);
      // This used to removeItem('token') on a 401 - a key nothing writes,
      // so it removed nothing and only looked like sign-out handling.
      setError(error.response?.data?.error || 'Could not load the SLA board');
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => { fetchSLAData(); }, [fetchSLAData]);

  const summary = useMemo(() => {
    const count = (s) => slaData.filter((d) => d.status === s).length;
    const onTrack = count('on_track');
    return {
      all: slaData.length,
      on_track: onTrack,
      at_risk: count('at_risk'),
      breached: count('breached'),
      completeRate: slaData.length > 0
        ? ((onTrack / slaData.length) * 100).toFixed(1)
        : 0
    };
  }, [slaData]);

  /*
   * Time left on a card. Since 114 the server counts it in WORKING hours on
   * the ticket's SLA profile (working_hours_remaining) - a ticket with one
   * working hour left on a Friday evening has one hour left, not sixty - and
   * says so. Against an older server, the gap to the deadline as before.
   */
  const calculateTimeRemaining = (item) => {
    const wh = item?.working_hours_remaining;
    if (wh !== null && wh !== undefined && Number.isFinite(Number(wh))) {
      const h = Number(wh);
      const unit = item?.sla_profile?.always_open ? 'h' : ' working h';
      const n = Math.abs(h) < 10 ? Math.round(Math.abs(h) * 10) / 10 : Math.round(Math.abs(h));
      if (h < 0) return `Overdue by ${n}${unit}`;
      return `${n}${unit} remaining${item?.clock_running === false ? ' (clock stopped)' : ''}`;
    }
    const now = new Date();
    const due = new Date(item?.resolution_deadline);
    const diff = due - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d remaining`;
    if (hours > 0) return `${hours}h remaining`;
    return 'Overdue';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_track': return 'status-on-track';
      case 'at_risk': return 'status-at-risk';
      case 'breached': return 'status-breached';
      default: return 'status-unknown';
    }
  };

  const rowsFor = (key) =>
    key === 'all' ? slaData : slaData.filter((item) => item.status === key);

  const filteredData = rowsFor(filter);

  // The SLA endpoint names its columns for this page; the shared drill-down
  // modal expects ticket-shaped rows, so translate rather than fork the modal.
  const asTickets = (rows) => rows.map((r) => ({
    id: r.id,
    ticket_number: r.ticket_number || r.ticket_id,
    title: r.subject,
    status: r.state,
    priority: r.priority,
    created_at: r.created_at
  }));

  const openDrilldown = (state) => {
    setDrilldown({
      title: state.key === 'all' ? 'All tracked tickets' : `${state.tile} tickets`,
      subtitle: state.key === 'all' ? 'every ticket under SLA' : `SLA status: ${state.tile}`,
      tickets: asTickets(rowsFor(state.key))
    });
  };

  if (loading) return <div className="sla-container loading">Loading SLA data...</div>;

  return (
    <div className="sla-container">
      {error && <p className="an-error" role="alert">{error}</p>}
      <header className="sla-header">
        <h1>SLA Tracking Dashboard</h1>
        <div className="sla-header-actions">
          <div className="sla-filters" role="group" aria-label="Filter by SLA status">
            {STATES.map((s) => (
              <button
                key={s.key}
                type="button"
                className="sla-filter-btn"
                aria-pressed={filter === s.key}
                onClick={() => setFilter(s.key)}
              >
                {s.label} ({summary[s.key]})
              </button>
            ))}
          </div>
          <button className="refresh-btn" onClick={fetchSLAData}>↻ Refresh</button>
        </div>
      </header>

      <div className="summary-cards">
        {STATES.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`summary-card ${s.className}`}
            onClick={() => openDrilldown(s)}
            title={`Show the ${s.tile.toLowerCase()} list`}
          >
            <div className="card-value">{summary[s.key]}</div>
            <div className="card-label">{s.tile}</div>
            <span className="card-hint">View tickets</span>
          </button>
        ))}
        <div className="summary-card compliance">
          <div className="card-value">{summary.completeRate}%</div>
          <div className="card-label">Compliance Rate</div>
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div className="empty-state">
          <p>No tickets to display</p>
        </div>
      ) : (
        <div className="sla-list">
          {filteredData.map(item => (
            <button
              type="button"
              key={item.ticket_id}
              className={`sla-card ${getStatusColor(item.status)}`}
              onClick={() => setPeek(item)}
              title="Open this ticket"
            >
              <div className="card-header">
                <div className="ticket-info">
                  {/* The number already begins JAR-, so "Ticket #" in
                      front of it read as "Ticket #JAR-2026-0001". And a
                      ticket with no number showed a raw UUID under that
                      label, which told nobody anything - the subject
                      underneath is the useful line, so show nothing. */}
                  {item.ticket_number && (
                    <span className="sla-card-id">{item.ticket_number}</span>
                  )}
                  <p className="ticket-title">{item.subject}</p>
                </div>
                <span className={`sla-state-badge ${getStatusColor(item.status)}`}>
                  {(item.status || 'unknown').replace('_', ' ').toUpperCase()}
                </span>
              </div>

              <div className="card-metrics">
                <div className="metric">
                  <label>Response SLA</label>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{width: `${Math.min((item.response_time_percent || 0), 100)}%`,background: item.response_status === 'met' ? 'var(--color-success)' : 'var(--color-warning)'}}
                    />
                  </div>
                  <span className="metric-text">
                    {item.response_time_hours?.toFixed(1)}h / {item.response_sla_hours}h target{item.sla_profile && !item.sla_profile.always_open ? ' (working)' : ''}
                  </span>
                </div>

                <div className="metric">
                  <label>Resolution SLA</label>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{width: `${Math.min((item.resolution_time_percent || 0), 100)}%`,background: item.resolution_status === 'met' ? 'var(--color-success)' : 'var(--color-danger)'}}
                    />
                  </div>
                  <span className="metric-text">
                    {item.resolution_time_hours?.toFixed(1)}h / {item.resolution_sla_hours}h target{item.sla_profile && !item.sla_profile.always_open ? ' (working)' : ''}
                  </span>
                </div>
              </div>

              <div className="card-details">
                <div className="detail">
                  <span className="label">Priority:</span>
                  <span className={`priority-${(item.priority || 'medium').toLowerCase()}`}>
                    {(item.priority || 'medium').toUpperCase()}
                  </span>
                </div>
                <div className="detail">
                  <span className="label">Created:</span>
                  <span>{fmtDate(item.created_at)}</span>
                </div>
                <div className="detail">
                  <span className="label">Time Remaining:</span>
                  <span className={item.resolution_deadline ? 'remaining' : 'completed'}>
                    {item.resolution_deadline
                      ? calculateTimeRemaining(item)
                      : 'Resolved'}
                  </span>
                </div>
                {item.sla_profile?.name && (
                  <div className="detail">
                    <span className="label">Hours:</span>
                    <span className="sla-profile-name">{item.sla_profile.name}</span>
                  </div>
                )}
              </div>

              {item.status === 'at_risk' && (
                <div className="alert at-risk">
                  ⚠ This ticket is approaching SLA deadline. Immediate action recommended.
                </div>
              )}

              {item.status === 'breached' && (
                <div className="alert breached">
                  ✗ SLA deadline has been breached. Escalation may be required.
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {drilldown && (
        <TicketListModal
          title={drilldown.title}
          subtitle={drilldown.subtitle}
          tickets={drilldown.tickets}
          onClose={() => setDrilldown(null)}
        />
      )}

      {peek && (
        <TicketPeekDetail
          ticketId={peek.id}
          ticketNumber={peek.ticket_number || peek.ticket_id}
          title={peek.subject}
          onClose={() => setPeek(null)}
          // The clocks belong to this page, not to the ticket, so they are
          // handed to the dialogue rather than fetched again inside it.
          extra={[
            { label: 'SLA status',
              value: (peek.status || 'unknown').replace('_', ' ') },
            { label: 'Response',
              value: `${(peek.response_time_hours ?? 0).toFixed(1)}h of ${peek.response_sla_hours}h` },
            { label: 'Resolution',
              value: `${(peek.resolution_time_hours ?? 0).toFixed(1)}h of ${peek.resolution_sla_hours}h` },
            { label: 'Time remaining',
              value: peek.resolution_deadline
                ? calculateTimeRemaining(peek)
                : 'Resolved' },
            ...(peek.sla_profile?.name ? [{ label: 'SLA hours', value: peek.sla_profile.name }] : [])
          ]}
        />
      )}
    </div>
  );
}
